/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  Component,
  inject,
  signal,
  viewChild,
  input,
  computed,
  linkedSignal
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {
  MainNavItemComponent,
  MainNavItemToggleEvent
} from './main-nav-item/main-nav-item.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { buyerNavItems, supplierNavItems } from './utils/mainSideNavUtils';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import {
  ProjectLimitPopoverModule,
  SustainmentButtonModule
} from 'sustainment-component';
import { ViewSwitcherService } from 'src/app/services/view-switcher.service';
import { UserAccountQuery } from 'src/app/store/userAccount/user-account.query';
import { PortalAppView } from 'sustainment-component/lib/injection-tokens/injectedTokenTypes';

@Component({
  selector: 'app-main-side-navigation',
  standalone: true,
  imports: [
    NgOptimizedImage,
    RouterLink,
    MainNavItemComponent,
    MenuModule,
    NgClass,
    SustainmentButtonModule,
    OverlayPanelModule,
    ProjectLimitPopoverModule
  ],
  templateUrl: './main-side-navigation.component.html',
  styleUrl: './main-side-navigation.component.scss'
})
export class MainSideNavigationComponent {
  private _router = inject(Router);
  private _viewSwitcherService = inject(ViewSwitcherService);
  private _userAccountQuery = inject(UserAccountQuery);

  public isAdmin = this._userAccountQuery.isAdmin;

  public canCreateProjects = input<boolean>(true);

  public currentAppView = this._viewSwitcherService.currentAppView;
  public currentUrl = signal<string>('');

  public currentlyOpenNavItems = linkedSignal<PortalAppView, string[]>({
    source: this.currentAppView,
    computation: () => []
  });

  public projectLimitOverlay = viewChild.required(OverlayPanel);

  public navItems = computed(() => {
    const isBuyerView = this.currentAppView() === 'buyer';
    const navItems = isBuyerView ? buyerNavItems : supplierNavItems;
    const alreadyHasNetwork = navItems.find((item) => item.title === 'Network');

    if (
      this._userAccountQuery.hasNetworkTabAccess &&
      isBuyerView &&
      !alreadyHasNetwork
    ) {
      navItems.splice(1, 0, {
        title: 'Network',
        url: ['network'],
        icon: 'ti ti-viewfinder'
      });
    }

    return navItems;
  });

  public createButtonMenuItems = computed<MenuItem[]>(() => {
    const currentAppView = this.currentAppView();

    switch (currentAppView) {
      case 'buyer':
        return [
          {
            label: 'RFQ',
            command: (event) => {
              if (!this.canCreateProjects()) {
                this.projectLimitOverlay()?.show(event.originalEvent);
              } else {
                this._router.navigate(['projects', 'sourcing-creation'], {
                  queryParams: { isSourcing: true }
                });
              }
            }
          },
          {
            label: 'PO',
            command: (event) => {
              if (!this.canCreateProjects()) {
                this.projectLimitOverlay()?.show(event.originalEvent);
              } else {
                this._router.navigate(['projects', 'procurement', 'drafts'], {
                  replaceUrl: true,
                  queryParams: { isSourcing: false, uploadPo: true }
                });

                this.currentlyOpenNavItems.update((current) => [
                  ...current,
                  'Procurement'
                ]);
              }
            }
          },
          {
            label: 'Supplier',
            command: () =>
              this._router.navigate(['suppliers', 'home'], {
                state: { shouldOpenAddModal: true }
              })
          },
          {
            label: 'Part',
            command: () =>
              this._router.navigate(['parts'], {
                state: { shouldOpenAddPart: true }
              })
          }
        ];
      case 'supplier':
        return [
          {
            label: 'Opportunity',
            command: () => this._router.navigate(['opportunities', 'creation'])
          },
          {
            label: 'Order',
            command: () =>
              this._router.navigate(['opportunities', 'order-creation'])
          },
          {
            label: 'Customer',
            command: () =>
              this._router.navigate(['customers', 'home'], {
                state: { shouldOpenAddModal: true }
              })
          }
        ];
      default: {
        const exhaustiveCheck: never = currentAppView;
        return exhaustiveCheck;
      }
    }
  });

  public constructor() {
    this.currentUrl.set(this._router.url);

    const matchingNavItemForCurrentRoute = this.navItems().find((navItem) => {
      const currentRoute = this.currentUrl().split('/').slice(1);
      return navItem.url.every((urlSegment, index) => {
        return urlSegment === currentRoute[index];
      });
    });

    if (matchingNavItemForCurrentRoute) {
      this.currentlyOpenNavItems.set([matchingNavItemForCurrentRoute.title]);
    }

    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((e) => {
        this.currentUrl.set((e as NavigationEnd).urlAfterRedirects);
      });
  }

  public onPanelToggled(e: MainNavItemToggleEvent): void {
    this.currentlyOpenNavItems.update((currentOpenItems) => {
      if (!e.hasSubNavItems) return currentOpenItems;
      if (e.isOpen) return [...currentOpenItems, e.title];

      return currentOpenItems.filter((item) => item !== e.title);
    });
  }
}
